import React, { useCallback, useRef, useEffect } from "react";
import { useFormik } from "formik";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import Directories from "../addFreeListingDirectories";
import { actions as homeActions } from "../../actions/home";
import styles from "./addFreeListing.module.css";

export const AddFreeListing = ({ isModal, listingModalListingType }) => {
  const dispatch = useDispatch();
  const getAddNewListingData = useCallback(isModalValue => dispatch(homeActions.getAddNewListingData(isModalValue)), [dispatch]);

  const navigate = useNavigate();
  const location = useLocation();
  const hasAutoSubmittedRef = useRef(false);
  const isMyEventsPage = location.pathname.includes("/my-events") || listingModalListingType.includes("/my-events");
  const isMyJobsPage = location.pathname.includes("/my-jobs") || listingModalListingType.includes("/my-jobs");
  const isMyOrganizationsPage = location.pathname.includes("/my-organizations") || listingModalListingType.includes("/my-organizations");
  const selectRef = useRef();

  const { addNewListingData } = useSelector(state => state.home);
  const professions = addNewListingData?.professions || [];
  const categories = addNewListingData?.categories || [];
  const addNewListing = useCallback(
    (data, listingType) => dispatch(homeActions.addNewListing(data, location.pathname, location.pathname, listingType, navigate)),
    [dispatch]
  ); // eslint-disable-line

  const handleSubmit = values => {
    const data = {
      category_id: values.category.category_id,
      listing_id: values.listing.listing_id,
      attorney: ""
    };

    addNewListing(data, values.profession.title);
  };

  const formik = useFormik({
    initialValues: {
      category: { category_id: "", profession_id: "", title: "Select" },
      listing: { listing_id: "", membership: "Select" },
      profession: { profession_id: "", title: "Select" },
      submitted: false
    },
    onSubmit: values => {
      handleSubmit(values);
    }
  });

  const handleChangeProfessional = newValue => {
    let professionIds = [newValue.profession_id].map(item => String(item));

    if (newValue.title.includes("Organizations")) {
      professionIds = professions.filter(item => item.title.includes("Organizations")).map(item => String(item.profession_id));
    }

    const newCategories = categories.filter(item => professionIds.includes(String(item.profession_id)));

    if (selectRef.current) {
      selectRef.current.focus();
      if (formik.values.profession.title !== newValue.title) {
        formik.setFieldValue("category", { category_id: "", profession_id: "", title: "Select" });
      }
    }

    formik.setFieldValue("profession", newValue);

    if (["Jobs", "Events", "Organizations"].includes(newValue.title) && newCategories.length) {
      handleChangeCategory(newCategories[0]);
    }

    handleSubmitListing();
  };

  const handleChangeCategory = newValue => {
    formik.setFieldValue("category", newValue);
  };

  useEffect(() => {
    if (!addNewListingData?.professions?.length || !addNewListingData?.categories?.length) {
      getAddNewListingData(Boolean(isModal));
    }
  }, [getAddNewListingData, addNewListingData?.professions?.length, addNewListingData?.categories?.length, isModal]);

  useEffect(() => {
    hasAutoSubmittedRef.current = false;
  }, [location.pathname]);

  useEffect(() => {
    if ((isMyJobsPage || isMyEventsPage || isMyOrganizationsPage) && professions.length) {
      let findCurrentProfession = null;

      if (isMyJobsPage) {
        findCurrentProfession = professions.find(item => item.title === "Jobs");
      } else if (isMyEventsPage) {
        findCurrentProfession = professions.find(item => item.title === "Events");
      } else {
        findCurrentProfession = professions.find(item => item.title?.includes("Organizations"));
      }

      if (findCurrentProfession) {
        handleChangeProfessional(findCurrentProfession);
      }
    }
  }, [isMyJobsPage, isMyEventsPage, isMyOrganizationsPage, professions]);

  useEffect(() => {
    if (
      !hasAutoSubmittedRef.current &&
      (isMyJobsPage || isMyEventsPage || isMyOrganizationsPage) &&
      formik.values.profession.profession_id &&
      formik.values.category.category_id
    ) {
      hasAutoSubmittedRef.current = true;
      formik.handleSubmit();
      formik.setFieldValue("submitted", true);
    }
  }, [
    formik,
    formik.values.category.category_id,
    formik.values.profession.profession_id,
    isMyEventsPage,
    isMyJobsPage,
    isMyOrganizationsPage
  ]);

  const handleSubmitListing = () => {
    if (!isMyOrganizationsPage && !isMyJobsPage && !isMyEventsPage) {
      formik.handleSubmit();
    }
  };

  return (
    <>
      {isMyJobsPage || isMyEventsPage || isMyOrganizationsPage ? null : (
        <>
          <div className={styles.content}>
            <h2 className={styles.title}>Add new listing</h2>
            <h3 className={styles.description}>Select listing type</h3>

            <Directories
              activeProfessional={formik.values.profession}
              professions={professions.filter(item => item.title.toLowerCase() !== "people")}
              handleChangeProfessional={handleChangeProfessional}
              isViewMore
              isModal={isModal}
            />
          </div>
        </>
      )}
    </>
  );
};
